<template>
  <v-card elevation="4" class="data-table services-table">
    <v-data-table
      calculate-widths
      :headers="headers"
      :items="services"
      :items-per-page.sync="itemsPerPage"
      :page.sync="page"
      :search="searchTerm"
      :sort-by="sortBy.value"
      :sort-desc="sortDesc"
      hide-default-footer
      hide-default-header
      class="elevation-0 custom-data-table"
      :server-items-length="itemsTotal"
      :loading="loading"
    >
      <template v-slot:item.plan_name="{ item }">
        <div class="additional-services">
          <div class="additional-services-main d-flex align-center">
            <p class="p-lg text-darken2 font-weight-bold mb-0">
              {{ item.plan_name }}
            </p>
            <!--   -->
            <CustomLabel
              v-if="item.status == 'cancelled'"
              :type="'expires'"
              class="ml-4"
            >
              {{ $t("general.cancelled") }}
            </CustomLabel>
            <CustomLabel
              v-else-if="item.status == 'suspended'"
              :type="'disabled'"
              class="ml-4"
            >
              {{ $t("general.suspended") }}
            </CustomLabel>
            <CustomLabel
              v-else-if="item.is_trial"
              :type="'expires'"
              class="ml-4"
            >
              {{ $t("Expires") }}
              {{ moment(item.trial_expiry_at).fromNow() }}
            </CustomLabel>
          </div>
          <AdditionalServices v-if="false" />
        </div>
      </template>

      <template v-slot:item.instances_count="{ item }">
        <CustomLabel :type="'purple'" class="radius-sm">
          {{ item.instances_count }}/{{ item.plan_settings.instance_limit }}
        </CustomLabel>
      </template>
      <template v-slot:item.instances="{ item }">
        <span v-if="!item.instances_count">-</span>
        <div v-else class="d-flex align-center">
          {{ item.instances[0].domain }}
          <v-tooltip
            v-if="item.instances.length > 1"
            open-delay="150"
            bottom
            transition="custom-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                class="ml-2 info--text cursor-pointer"
                v-on="on"
                v-bind="attrs"
              >
                + {{ item.instances.length - 1 }} {{ $t("more") }}
              </div>
            </template>
            <div>
              <p class="p-sm text--darken2 font-weight-bold">
                {{ $t("general.sites") }} ({{ item.instances.length }})
              </p>
              <v-divider class="mt-2 mb-3" />
              <ul>
                <li
                  class="d-flex space-between my-2"
                  v-for="instance in item.instances"
                  :key="instance.id"
                >
                  <span class="mr-2">{{ instance.domain }}</span>
                  <CustomLabel
                    v-if="instance.domain_type"
                    class="justify-end ml-a"
                    :type="domainTypes[instance.domain_type].key"
                  >
                    {{ domainTypes[instance.domain_type].text }}
                  </CustomLabel>
                </li>
              </ul>
            </div>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="base-actions">
          <CancelServiceButton
            v-if="item.status != 'cancelled'"
            :service="item"
            class="v-btn"
            v-on="$listeners"
          />
          <v-btn
            v-if="item.plan_upgrade_link"
            :href="item.plan_upgrade_link"
            target="_blank"
            :color="item.is_trial ? 'orange' : 'primary'"
            elevation="0"
            small
          >
            <span :class="item.is_trial ? 'white--text' : ''">{{
              $t("button.upgrade")
            }}</span>
          </v-btn>
        </div>
        <AdditionalActions v-if="false" />
      </template>
      <template v-slot:body v-if="loading">
        <template v-if="$vuetify.breakpoint.lgAndDown">
          <services-table-item-skeleton-mobile v-for="item in 5" :key="item" />
        </template>
        <template v-else>
          <services-table-skeleton-sort />
          <services-table-skeleton-item />
        </template>
      </template>

      <template v-slot:no-data>
        <template v-if="!loading && !searchTerm">
          <table-full-screen-message :title="$t('message.emptyTable.services.title')">
          </table-full-screen-message>
        </template>
        <data-iterator-no-results-container
          v-if="searchTerm"
          @clearAllFilters="$emit('update:searchTerm', '')"
          :searchTerm="searchTerm"
        />
      </template>

      <template v-if="!loading" v-slot:header="{ isMobile, props, on }">
        <table-custom-sort
          :isMobile="isMobile"
          :props="props"
          :v-on="on"
          v-on="$listeners"
        />
      </template>

      <template v-slot:footer>
        <div>
          <data-iterator-footer
            :page="page"
            :numberOfPages="numberOfPages"
            :possibleItemsPerPage="possibleItemsPerPage"
            :listStyle="listStyle"
            :itemsPerPage="itemsPerPage"
            :itemsTotal="itemsTotal"
            v-on="$listeners"
            :perPageLabel="$t('table.footer.plan')"
          >
          </data-iterator-footer>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import moment from "moment";
import DataIteratorMixin from "../../../mixins/DataIteratorMixin";
import CustomTableMixin from "../../../mixins/CustomTableMixin";

import DataIteratorFooter from "../DataIteratorFooter.vue";
import TableCustomSort from "../TableCustomSort.vue";

import ServicesTableSkeletonItem from "./ServicesTableSkeletonItem.vue";
import ServicesTableSkeletonSort from "./ServicesTableSkeletonSort.vue";
import ServicesTableItemSkeletonMobile from "./ServicesTableItemSkeletonMobile.vue";
import TableFullScreenMessage from "../tables/TableFullScreenMessage.vue";
import DataIteratorNoResultsContainer from "../DataIteratorNoResultsContainer.vue";

import CustomLabel from "../../labels/CustomLabel.vue";
import AdditionalServices from "./AdditionalServices.vue";
import AdditionalActions from "./AdditionalActions.vue";
import CancelServiceButton from "../../buttons/CancelServiceButton.vue";

export default {
  components: {
    DataIteratorFooter,
    TableCustomSort,
    ServicesTableSkeletonItem,
    ServicesTableSkeletonSort,
    ServicesTableItemSkeletonMobile,
    TableFullScreenMessage,
    DataIteratorNoResultsContainer,
    CustomLabel,
    AdditionalServices,
    AdditionalActions,
    CancelServiceButton,
  },
  mixins: [DataIteratorMixin, CustomTableMixin],
  props: {
    services: Array,
    loading: Boolean,
    searchTerm: String,
    itemsTotal: Number,
    items: Array,
  },
  data() {
    return {
      moment,
      itemsLoading: false,
      headers: [
        {
          text: this.$t("plan_name"),
          value: "plan_name",
          sortable: true,
        },
        {
          text: this.$t("instance_limit"),
          value: "instances_count",
          sortable: true,
        },
        {
          text: this.$t("sites"),
          value: "instances",
          sortable: true,
        },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
        },
      ],
      domainTypes: {
        main: {
          key: "maindomain",
          text: this.$t("heading.hosting.domains.domain.main"),
        },
        addon: {
          key: "addon",
          text: this.$t("heading.hosting.domains.domain.addon"),
        },
        sub: {
          key: "subdomain",
          text: this.$t("heading.hosting.domains.domain.sub"),
        },
        alias: {
          key: "alias",
          text: this.$t("heading.hosting.domains.domain.alias"),
        },
      },
    };
  },
  computed: {
    pageCount() {
      return Math.ceil(this.items.length / this.itemsPerPage);
    },
  },
};
</script>

<style lang="scss">
.services-table {
  tbody {
    tr {
      td {
        // vertical-align: center;
        // padding-top: 16px !important;
        &:last-child {
          // padding-top: 6px!important;
          padding-right: 16px !important;
          .base-actions{
            // margin-top: -10px;
          }
        }
      }
    }
  }
  .additional-services-main {
  }
  .additional-services-list {
    margin-top: 16px;
    padding-bottom: 10px;
    > ul {
      position: relative;
      &:before {
        content: "";
        width: 1px;
        position: absolute;
        top: -5px;
        bottom: 12px;
        left: 0;
        background-color: var(--v-gray-lighten2);
      }
      > li {
        padding-left: 24px;
        position: relative;
        height: 24px;
        &:not(:first-child) {
          margin-top: 12px;
        }
        &:before {
          content: "";
          height: 1px;
          position: absolute;
          top: calc(50% - 1px);
          left: 0;
          width: 16px;
          background-color: var(--v-gray-lighten2);
        }
      }
    }
  }
  .additional-actions {
    padding-bottom: 10px;
    margin-top: 30px;
    .actions-set {
      &:not(:first-child) {
        margin-top: 11px;
      }
      .v-size--x-small {
        padding-left: 10px !important;
        padding-right: 10px !important;
        font-size: 13px;
        line-height: 18px;
        min-width: 76px;
        height: 26px;
      }
    }
  }
  tbody {
    tr {
      td {
        // display: flex;
        // align-items: center;
      }
    }
  }
}
</style>